import { Component, Input, computed, inject, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ProductFact } from '../../../../../core/models/editorialContent.model';

interface RenderedFact {
  key:        string;
  label:      string;
  value:      ProductFact['value'];
  valueLabel: string;
}

/**
 * Product information, rendered as a tri-state.
 *
 * The three states are not symmetric and the sheet must not flatten them:
 *
 *  - `true` / `false` are verified claims, each backed by a source and a date;
 *  - `unknown` means a directed search looked and found nothing. It is NOT a `false`, and
 *    showing it as one would state that a producer declared an absence they never declared.
 *
 * That is exactly the ambiguity of the legacy catalogue booleans this block exists to fix, so
 * each state gets its own mark and its own wording rather than a checkbox.
 *
 * The evidence date is deliberately NOT rendered. It still travels in the contract and still
 * backs the claim in admin, but on the public sheet a date beside every row turns verification
 * into a visual claim the guest is asked to audit ("is 2024-11-04 recent enough?"). Verification
 * is a property of the system, not a badge: the sheet either states a fact or says it could not
 * confirm it. Same reason the block reads "product information" in public rather than "verified
 * data" — a guest reads facts about a bottle, not a report about our pipeline.
 */
@Component({
  selector: 'app-spirit-product-facts',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './spirit-product-facts.component.html',
  styleUrl: './spirit-product-facts.component.scss'
})
export class SpiritProductFactsComponent {

  private translate = inject(TranslateService);

  private source = signal<ProductFact[]>([]);

  @Input({ required: true }) set facts(value: ProductFact[] | null | undefined) {
    this.source.set(value ?? []);
  }

  rendered = computed<RenderedFact[]>(() =>
    this.source().map(fact => ({
      key:        fact.key,
      label:      this.labelOf(fact.key),
      value:      fact.value,
      valueLabel: this.translate.instant('editorial.fact_value.' + fact.value)
    }))
  );

  /** A key with no translation yet is shown as its own key rather than dropped. */
  private labelOf(key: string): string {
    const translationKey = 'editorial.fact.' + key;
    const translated = this.translate.instant(translationKey);

    return translated && translated !== translationKey ? translated : key;
  }
}
